<template>
  <w-modal class="voiceSelectorModal" :active="active" :max-width="'800px'" :full-height="false" @close="close">
    <div class="voiceSelector">
      <w-button class="voiceSelector__close"
                color="text"
                size="small"
                icon="close"
                @click="close"/>
      <h2 class="voiceSelector__title">{{ $t('voiceSelector.title') }}</h2>

      <div class="voiceSelector__filters">
        <w-dropdown v-if="showKindFilter" class="voiceSelector__dropdown-filter"
                    size="small" :items="voiceKindOptions" v-model="voiceKind"/>
        <w-dropdown class="voiceSelector__dropdown-filter"
                    size="small" :items="useCaseOptions" v-model="useCaseFilter"/>
        <div class="voiceSelector__fav-toggle">
          <span class="voiceSelector__fav-label">{{ $t('voiceSelector.favorites') }}</span>
          <w-switch class="voiceSelector__fav-switch" v-model="showFavoritesOnly"/>
        </div>
        <w-button class="voiceSelector__clear" :color="'outlined'" :enabled="hasActiveFilter" @click="clearFilters" size="small">
          {{ $t('voiceSelector.clearFilters') }}
        </w-button>
      </div>

      <div class="voiceSelector__grid">
        <div v-for="voice in filteredVoices" :key="voice.id"
             class="voiceCard"
             :class="{'voiceCard--selected': voice.id === selectedVoiceId, 'voiceCard--disabled': isDisabled(voice)}"
             @click="selectVoice(voice)">
          <w-button class="voiceCard__play" color="outlined"
                    :icon="playingVoiceId === voice.id ? 'stop' : 'play'"
                    :loading="loadingVoiceId === voice.id"
                    :enabled="!isDisabled(voice)"
                    @click.stop="previewVoice(voice)"/>
          <div class="voiceCard__info">
            <div class="voiceCard__name">
              {{ voice.label }}
            </div>
            <w-tool-tip position="top" :expanded="true"
                        :text-label="tooltipVoiceId === voice.id ? voiceMeta(voice) : ''">
              <div class="voiceCard__meta" @mouseenter="onMetaHover($event, voice)">{{ voiceMeta(voice) }}</div>
            </w-tool-tip>
          </div>
          <w-button class="voiceCard__fav"
                    :class="{'voiceCard__fav--active': isFavorite(voice.id)}"
                    color="text"
                    size="small"
                    :icon="isFavorite(voice.id) ? 'star' : 'star-outline'"
                    @click.stop="toggleFavorite(voice.id)"/>
        </div>
      </div>

      <div class="voiceSelector__footer">
        <w-button :color="'outlined'" @click="close">{{ $t('voiceSelector.cancel') }}</w-button>
        <w-button :icon="'check'" :enabled="!!selectedVoiceId" @click="validate">
          {{ $t('voiceSelector.validate') }}
        </w-button>
      </div>
    </div>
  </w-modal>
</template>

<script lang="ts">
import Vue from 'vue';
import {Component, Prop, Watch} from 'vue-property-decorator';
import {Howl} from 'howler';
import store from '@/store';
import WModal from '@/components/wrapper/w-modal.vue';
import WButton from '@/components/wrapper/w-button.vue';
import WSwitch from '@/components/wrapper/w-switch.vue';
import WIcon from '@/components/wrapper/w-icon.vue';
import WToolTip from '@/components/wrapper/w-toolTip.vue';
import WDropdown from '@/components/wrapper/w-dropdown.vue';
import {getProviderForVoice, getVoiceCatalog, TTSCatalog, VoiceDescriptor} from '@/store/media/ttsCatalogModel';
import {getVoicePreviewURL} from '@/utils/voicePreviewUrl';
import {SELECT_AI_VOICE} from '@/store/AIAvatar/AIAVatarAction';

const FAVORITES_KEY = 'tts_favorite_voices';

@Component({
  components: {WModal, WButton, WSwitch, WIcon, WToolTip, WDropdown}
})
export default class VoiceSelectorModal extends Vue {
  // Whether the modal is open (sync with parent).
  @Prop({default: false})
  private active!: boolean;

  // Currently selected voice id, to preselect when opening.
  @Prop({default: ''})
  private voice!: string;

  // Restrict the list to a gender (MALE/FEMALE/NEUTRAL). Empty = show all genders.
  @Prop({default: ''})
  private gender!: string;

  // Effective locale (accent over base language) used to synthesize the preview. Null = auto-detect.
  @Prop({default: null})
  private language!: string | null;

  private selectedVoiceId: string = '';
  // Which kind of voice the list shows : standard (free) or advanced (AI credits). Never both at once.
  private voiceKind: 'STANDARD' | 'ADVANCED' = 'STANDARD';
  private useCaseFilter: string = '';
  private showFavoritesOnly: boolean = false;
  private favorites: string[] = [];

  private playingVoiceId: string | null = null;
  private loadingVoiceId: string | null = null;
  private audioPlayer: Howl | null = null;

  // Card whose meta is truncated under the cursor, drives its tooltip.
  private tooltipVoiceId: string = '';

  private created() {
    this.loadFavorites();
    this.selectedVoiceId = this.voice;
    this.syncKindToSelectedVoice();
  }

  @Watch('voice')
  private onVoiceChange() {
    this.selectedVoiceId = this.voice;
    this.syncKindToSelectedVoice();
  }

  @Watch('active')
  private onActiveChange() {
    if (this.active) {
      this.selectedVoiceId = this.voice;
      this.revertAdvancedIfNoCredits();
      this.syncKindToSelectedVoice();
    } else {
      this.stopPreview();
    }
  }

  // If the currently selected voice is advanced and the user has run out of credits, swap it for a free standard voice
  // (same gender when possible) and persist the change. Selection sticks until this point (normally won't be reached,
  // if the user runs out of credit and reloads the page he won't be able to generate anything, even with free voices).
  // See the avatar source "mount" restore.
  private revertAdvancedIfNoCredits() {
    const rawCredits = store.getters.getRemainingAiCredits;
    const isAdvanced = getProviderForVoice(this.selectedVoiceId) === 'GEMINI';
    if (this.hasCredits) return;
    if (!isAdvanced) return;
    const current = this.catalog.voices.find((v) => v.id === this.selectedVoiceId);
    const wantedGender = current ? current.gender : '';
    const sameGender = wantedGender
      ? this.catalog.voices.find((v) => !v.advanced && v.gender === wantedGender)
      : undefined;
    const fallback = sameGender || this.catalog.voices.find((v) => !v.advanced);
    if (!fallback) return;
    this.selectedVoiceId = fallback.id;
    store.dispatch(SELECT_AI_VOICE, fallback.id);
  }

  // Open the kind dropdown on the kind of the currently-selected voice, so it shows up in the list.
  private syncKindToSelectedVoice() {
    const selected = this.catalog.voices.find((v) => v.id === this.selectedVoiceId);
    this.voiceKind = selected && selected.advanced ? 'ADVANCED' : 'STANDARD';
  }

  // The kind dropdown only makes sense when advanced voices exist (tenant has advanced TTS on).
  get showKindFilter(): boolean {
    return store.getters.isAdvancedTtsEnabled;
  }

  // Options for the kind dropdown. "Advanced" shows a hint that it spends AI credits, and is
  // disabled when the user has run out of credits (cannot start a new generation).
  get voiceKindOptions() {
    return [
      { value: 'STANDARD', label: this.$t('voiceSelector.standardVoices') },
      {
        value: 'ADVANCED',
        label: this.$t('voiceSelector.advancedVoices'),
        description: this.$t('voiceSelector.advancedVoicesHint'),
        disabled: !this.hasCredits,
        icon: 'star-four-points-circle-outline',
        iconColor: 'warning',
        tooltip: this.$t('generic.aiCreditsTooltip'),
        tooltipInteractive: true,
        tooltipPosition: 'right',
      }
    ];
  }

  get catalog(): TTSCatalog {
    return getVoiceCatalog(store.getters.isAdvancedTtsEnabled);
  }

  // Paid (Advanced) voices are unavailable when the user has no AI credits left. Null/undefined credits
  // mean "not limited", so we only block on a real zero/negative.
  get hasCredits(): boolean {
    const credits = store.getters.getRemainingAiCredits;
    return credits === null || credits === undefined || credits > 0;
  }

  public isDisabled(voice: VoiceDescriptor): boolean {
    return voice.advanced && !this.hasCredits;
  }

  // Base voice set feeding both the use-case options and the list: the picked kind (standard/advanced)
  // and, when set, the requested gender. Filtering both here keeps neither dropdown offering a value
  // that yields no voices.
  get genderVoices(): VoiceDescriptor[] {
    const wantAdvanced = this.voiceKind === 'ADVANCED';
    return this.catalog.voices.filter((v) =>
      v.advanced === wantAdvanced && (this.gender === '' || v.gender === this.gender));
  }

  // Only use-cases present among the gender voices, so the filter never yields an empty list.
  get useCases(): string[] {
    const all: string[] = [];
    for (const voice of this.genderVoices) {
      for (const useCase of voice.useCases) {
        if (all.indexOf(useCase) === -1) {
          all.push(useCase);
        }
      }
    }
    return all;
  }

  // Items for the use-case dropdown: "All use cases" (empty value = no filter) followed by each use-case.
  get useCaseOptions(): any[] {
    return [
      {value: '', label: this.$t('voiceSelector.allUseCases') as string},
      ...this.useCases.map((useCase) => ({value: useCase, label: useCase}))
    ];
  }

  get hasActiveFilter(): boolean {
    return this.voiceKind !== 'STANDARD' || this.useCaseFilter !== '' || this.showFavoritesOnly;
  }

  get filteredVoices(): VoiceDescriptor[] {
    let list = this.genderVoices;
    if (this.useCaseFilter !== '') {
      list = list.filter((v) => v.useCases.indexOf(this.useCaseFilter) !== -1);
    }
    if (this.showFavoritesOnly) {
      list = list.filter((v) => this.isFavorite(v.id));
    }
    return list;
  }

  // Enable the meta tooltip only when the text is actually truncated (checked on hover).
  public onMetaHover(event: MouseEvent, voice: VoiceDescriptor) {
    // Truncated when the text needs more than the 2 clamped lines (2.5 = 2 lines + half-line margin).
    const el = event.currentTarget as HTMLElement;
    const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
    this.tooltipVoiceId = el.scrollHeight > lineHeight * 2.5 ? voice.id : '';
  }

  public voiceMeta(voice: VoiceDescriptor): string {
    // The card meta lists the voice's use-cases.
    return voice.useCases.join(', ');
  }

  public selectVoice(voice: VoiceDescriptor) {
    if (this.isDisabled(voice)) {
      return;
    }
    this.selectedVoiceId = voice.id;
  }

  // FAVORITES (stored client-side only)
  private loadFavorites() {
    try {
      this.favorites = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    } catch (e) {
      this.favorites = [];
    }
  }

  public isFavorite(id: string): boolean {
    return this.favorites.indexOf(id) !== -1;
  }

  public toggleFavorite(id: string) {
    if (this.isFavorite(id)) {
      this.favorites = this.favorites.filter((favId) => favId !== id);
    } else {
      this.favorites = [...this.favorites, id];
    }
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(this.favorites));
  }

  public clearFilters() {
    this.voiceKind = 'STANDARD';
    this.useCaseFilter = '';
    this.showFavoritesOnly = false;
  }

  // PREVIEW: play the pre-generated static MP3 served via the media CDN. No on-demand synthesis, so this is free and instant.
  // If the file is missing, fires onloaderror -> silent log, no fallback.
  public previewVoice(voice: VoiceDescriptor) {
    if (this.isDisabled(voice)) {
      return;
    }
    if (this.playingVoiceId === voice.id) {
      this.stopPreview();
      return;
    }
    this.stopPreview();
    const url = getVoicePreviewURL(voice.provider, this.language, voice.id);
    if (!url) {
      return;
    }
    this.loadingVoiceId = voice.id;
    this.audioPlayer = new Howl({
      src: [url],
      autoplay: true,
      html5: true,
      format: ['mp3'],
      volume: 1,
      onplay: () => {
        this.loadingVoiceId = null;
        this.playingVoiceId = voice.id;
      },
      onend: () => {
        this.playingVoiceId = null;
      },
      onloaderror: (_id, err) => {
        // tslint:disable-next-line:no-console
        console.warn(`[voice-preview] failed to load ${url}:`, err);
        this.loadingVoiceId = null;
        this.playingVoiceId = null;
      }
    });
  }

  public stopPreview() {
    if (this.audioPlayer) {
      this.audioPlayer.stop();
      this.audioPlayer = null;
    }
    this.playingVoiceId = null;
    this.loadingVoiceId = null;
  }

  public validate() {
    this.stopPreview();
    // The accent is owned by the panel now; the modal only returns the chosen voice.
    this.$emit('validate', { voice: this.selectedVoiceId });
    this.close();
  }

  public close() {
    this.stopPreview();
    this.$emit('update:active', false);
    this.$emit('close');
  }

  private beforeDestroy() {
    this.stopPreview();
  }
}
</script>

<style scoped lang="scss">
.voiceSelector {
  position: relative;
  padding: 24px;
  background-color: var(--white);
  border-radius: 12px;

  &__close {
    position: absolute;
    top: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--black);
    font-size: 20px;
  }

  &__title {
    text-align: center;
    font-weight: 900;
    font-size: 24px;
    margin-bottom: 24px;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  &__filters &__dropdown-filter {
    width: 190px;
    margin-bottom: 0;
  }

  &__fav-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__fav-switch {
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }

  &__fav-label {
    white-space: nowrap;
    font-weight: 600;
    font-size: 16px;
  }

  &__clear {
    margin-left: auto;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    max-height: 420px;
    overflow-y: auto;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
  }
}

.voiceCard {
  display: flex;
  align-items: center;
  width: 368px;
  height: 72px;
  gap: 16px;
  padding: 12px 16px;
  border: 1px solid var(--light1);
  background-color: var(--white);
  border-radius: 16px;
  cursor: pointer;
  min-width: 0;
  transition: border-color 0.15s, background-color 0.15s;

  opacity: 1;
  left: 24px;


  &:not(.voiceCard--disabled):hover {
    background-color: var(--light);
  }

  &:not(.voiceCard--disabled):active {
    background-color: var(--light1);
  }

  &--selected {
    border-color: var(--primary);
    background-color: var(--light1);
  }

  // Paid voice with no credits left: greyed out and not selectable.
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__play {
    flex-shrink: 0;

    ::v-deep .button {
      width: 48px;
      padding: 0;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 2px;
  }

  &__name {
    font-weight: 700;
    font-size: 16px;
  }

  &__meta {
    color: var(--light2);
    font-size: 14px;
    font-weight: 600;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__fav {
    display: inline-flex;
    visibility: hidden;
    background: none;
    border: none;
    cursor: pointer;
    flex-shrink: 0;

    &--active {
      visibility: visible;

      // the favorite star is colored here, because w-button's color styles the button itself
      ::v-deep .icon {
        color: var(--warning);
      }
    }
  }

  &:hover &__fav, &--selected &__fav {
    visibility: visible;
  }
}
</style>

<style lang="scss">
.voiceSelectorModal {
  // Lock the voice-kind dropdown menu to a fixed width so the "Advanced voices" description wraps consistently.
  .dropdown.is-expanded .dropdown-menu {
    width: 240px;
  }
}
</style>
